import React, { useState } from "react";
import ReactDOM from "react-dom";
import { HiCheck, HiX } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";

const ApplicantModal = ({
  applicant,
  onClose,
  getDisplayName,
  formatDate,
  handleDM,
  updateStatus,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null); // "Approved" | "Rejected"

  if (!applicant) return null;

  const handleStatusUpdate = async (status) => {
    setIsLoading(true);
    try {
      await updateStatus(applicant.userId, status);
      setIsLoading(false);
      setResult(status);
      setTimeout(() => {
        setResult(null);
        onClose();
      }, 2500);
    } catch (err) {
      console.error("❌ Error updating status:", err);
      setIsLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999] transition-opacity duration-300">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 20 }}
        className="bg-white rounded-xl w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh] relative shadow-xl"
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          disabled={isLoading}
        >
          ✕
        </button>

        {/* Applicant Header */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src={applicant.profileImage}
            alt="Avatar"
            className="w-12 h-12 rounded-full border object-cover"
          />
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {getDisplayName(applicant)}
            </h2>
            <p className="text-sm text-gray-500">
              Applied: {formatDate(applicant.appliedAt)}
            </p>
          </div>
        </div>

        {/* Agreements */}
        <div className="bg-gray-50 rounded-lg p-3 shadow-sm mb-4">
          <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Agreements
          </h4>
          <div className="flex gap-6 text-sm">
            <div>
              <p className="text-gray-600">Time Commitment</p>
              <p
                className={`font-medium ${
                  applicant.agreements?.timeCommitment
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {applicant.agreements?.timeCommitment
                  ? "Confirmed"
                  : "Not Confirmed"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Skills</p>
              <p
                className={`font-medium ${
                  applicant.agreements?.skills
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {applicant.agreements?.skills
                  ? "Confirmed"
                  : "Not Confirmed"}
              </p>
            </div>
          </div>
        </div>

        {/* Answers */}
        {Array.isArray(applicant.questions) &&
          applicant.questions.length > 0 && (
            <div className="bg-white rounded-lg p-3 shadow-sm border mb-4">
              <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                Answers
              </h4>
              <div className="space-y-2">
                {applicant.questions.map((q, i) => (
                  <div key={i}>
                    <p className="text-gray-600 font-medium text-sm">{q}</p>
                    <p className="ml-2 text-gray-900 text-sm">
                      {applicant.answers?.[i] || "No Answer"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Links */}
        {Array.isArray(applicant.links) && applicant.links.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 shadow-sm mb-4">
            <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              Submitted Links
            </h4>
            <div className="flex flex-wrap gap-2">
              {applicant.links.map((l, i) => (
                <a
                  key={i}
                  href={l}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100 transition"
                >
                  {new URL(l).hostname}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Extra Notes */}
        {applicant.extraNotes && (
          <div className="bg-white rounded-lg p-3 shadow-sm border mb-4">
            <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              Extra Notes
            </h4>
            <p className="text-gray-800 text-sm leading-relaxed">
              {applicant.extraNotes}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <button
            className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-blue-700 transition"
            onClick={() => handleDM(applicant)}
            disabled={isLoading}
          >
            DM
          </button>
          {applicant.status === "Pending" && (
            <>
              <button
                onClick={() => handleStatusUpdate("Approved")}
                className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-green-700 transition"
                disabled={isLoading}
              >
                <HiCheck className="inline mr-1" /> Approve
              </button>
              <button
                onClick={() => handleStatusUpdate("Rejected")}
                className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-red-700 transition"
                disabled={isLoading}
              >
                <HiX className="inline mr-1" /> Reject
              </button>
            </>
          )}
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl z-10">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-sm text-gray-700 mt-2 font-medium">Processing...</p>
          </div>
        )}

        {/* Final Result Modal */}
        <AnimatePresence>
          {result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 backdrop-blur-lg rounded-xl z-20"
            >
              {result === "Approved" ? (
                <>
                  <HiCheck className="text-green-500 text-5xl mb-2" />
                  <p className="text-lg font-semibold text-green-700">Applicant Approved</p>
                  <p className="text-gray-500 text-sm mt-1">
                    They’ve been added to your team.
                  </p>
                </>
              ) : (
                <>
                  <HiX className="text-red-500 text-5xl mb-2" />
                  <p className="text-lg font-semibold text-red-700">Application Rejected</p>
                  <p className="text-gray-500 text-sm mt-1">
                    The applicant has been notified.
                  </p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>,
    document.body
  );
};

export default ApplicantModal;
